import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import axiosInstant from '../../api';
import {
    publicOrderFeedbackForm,
    publicSubmitOrderFeedback,
} from '../../api/endpoints';
import { entityKey, ORDER_QUESTIONS, PROJECT_QUESTIONS } from './constants';
import {
    EntityType,
    FeedbackForm,
    ProjectSection,
    ScoredEntity,
    Scores,
    Step,
    StepKind,
} from './types';

const projectCommentKey = (projectId: number) => `project_${projectId}`;

const collectSavedEntityScores = (form: FeedbackForm) => {
    const scores: Record<string, number> = {};
    const remember = (entityType: EntityType, entity: ScoredEntity) => {
        if (entity.score) scores[entityKey(entityType, entity.id)] = entity.score;
    };
    (form.projects || []).forEach((project) => {
        (project.channels || []).forEach((channel) => {
            remember('channel', channel);
            (channel.assets || []).forEach((asset) => remember('asset', asset));
        });
    });
    return scores;
};

const buildSteps = (form: FeedbackForm | null): Step[] => {
    if (!form) return [];
    return [
        { kind: 'order', label: 'Experience' },
        ...form.projects.map((_project, index) => ({
            kind: 'project' as StepKind,
            label: `Project ${index + 1}`,
            projectIndex: index,
        })),
        { kind: 'submit' as StepKind, label: 'Submit' },
    ];
};

export interface KnownIdentity {
    name: string;
    email: string;
}

export const useOrderFeedback = (
    token?: string,
    identity?: KnownIdentity | null,
) => {
    const [form, setForm] = useState<FeedbackForm | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [invalid, setInvalid] = useState(false);
    const [step, setStep] = useState(0);
    const [orderScores, setOrderScores] = useState<Scores>({});
    const [projectScores, setProjectScores] = useState<Record<number, Scores>>({});
    const [comments, setComments] = useState<Record<string, string>>({});
    const [entityScores, setEntityScores] = useState<Record<string, number>>({});
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');

    useEffect(() => {
        if (!token) return;
        setLoading(true);
        axiosInstant
            .get(publicOrderFeedbackForm(token))
            .then(({ data }: { data: FeedbackForm }) => {
                setForm(data);
                setOrderScores(data.answers || {});
                setEntityScores(collectSavedEntityScores(data));
                setName(data.submitted_by_name || '');
                setEmail(data.submitted_by_email || '');

                const nextComments: Record<string, string> = {
                    order: data.answers?.comments || '',
                };
                const nextProjects: Record<number, Scores> = {};
                (data.projects || []).forEach((project) => {
                    nextProjects[project.id] = project.answers || {};
                    nextComments[projectCommentKey(project.id)] =
                        project.answers?.comments || '';
                });
                setProjectScores(nextProjects);
                setComments(nextComments);
            })
            .catch(() => setInvalid(true))
            .finally(() => setLoading(false));
    }, [token]);

    const steps = useMemo(() => buildSteps(form), [form]);

    const entityScore = useCallback(
        (entityType: EntityType, entityId: number): number | null =>
            entityScores[entityKey(entityType, entityId)] ?? null,
        [entityScores],
    );

    const setEntityScore = useCallback(
        (entityType: EntityType, entityId: number, value: number) => {
            setEntityScores((prev) => ({
                ...prev,
                [entityKey(entityType, entityId)]: value,
            }));
        },
        [],
    );

    const setOrderScore = useCallback((field: string, value: number) => {
        setOrderScores((prev) => ({ ...prev, [field]: value }));
    }, []);

    const setProjectScore = useCallback(
        (projectId: number, field: string, value: number) => {
            setProjectScores((prev) => ({
                ...prev,
                [projectId]: { ...(prev[projectId] || {}), [field]: value },
            }));
        },
        [],
    );

    const setComment = useCallback((key: string, value: string) => {
        setComments((prev) => ({ ...prev, [key]: value }));
    }, []);

    const orderComment = comments.order || '';
    const projectComment = (projectId: number) =>
        comments[projectCommentKey(projectId)] || '';

    const stepDone = useMemo(
        () =>
            steps.map((entry) => {
                if (entry.kind === 'order') {
                    return ORDER_QUESTIONS.every((q) => orderScores[q.field]);
                }
                if (entry.kind === 'project') {
                    const project = form?.projects[entry.projectIndex as number];
                    return Boolean(
                        project
                        && PROJECT_QUESTIONS.every(
                            (q) => projectScores[project.id]?.[q.field],
                        ),
                    );
                }
                if (identity) return true;
                return Boolean(name.trim() && email.trim());
            }),
        [steps, orderScores, projectScores, entityScore, form, name, email, identity],
    );

    const progress = useMemo(() => {
        if (!form) return 0;
        let total = ORDER_QUESTIONS.length;
        let answered = ORDER_QUESTIONS.filter((q) => orderScores[q.field]).length;

        form.projects.forEach((project) => {
            total += PROJECT_QUESTIONS.length;
            answered += PROJECT_QUESTIONS.filter(
                (q) => projectScores[project.id]?.[q.field],
            ).length;
            project.channels.forEach((channel) => {
                total += 1 + channel.assets.length;
                if (entityScore('channel', channel.id)) answered += 1;
                answered += channel.assets.filter((a) =>
                    entityScore('asset', a.id),
                ).length;
            });
        });

        return total ? Math.round((answered / total) * 100) : 0;
    }, [form, orderScores, projectScores, entityScore]);

    const buildPayload = useCallback(
        () => ({
            ...ORDER_QUESTIONS.reduce(
                (acc, question) => ({
                    ...acc,
                    [question.field]: orderScores[question.field] ?? null,
                }),
                {} as Scores,
            ),
            comments: comments.order || null,
            submitted_by_name: identity ? identity.name : name,
            submitted_by_email: identity ? identity.email : email,
            projects: (form?.projects || []).map((project: ProjectSection) => ({
                id: project.id,
                ...PROJECT_QUESTIONS.reduce(
                    (acc, question) => ({
                        ...acc,
                        [question.field]:
                            projectScores[project.id]?.[question.field] ?? null,
                    }),
                    {} as Scores,
                ),
                comments: comments[projectCommentKey(project.id)] || null,
                channels: (project.channels || []).map((channel) => ({
                    id: channel.id,
                    score: entityScore('channel', channel.id),
                    assets: (channel.assets || []).map((asset) => ({
                        id: asset.id,
                        score: entityScore('asset', asset.id),
                    })),
                })),
            })),
        }),
        [
            form,
            orderScores,
            projectScores,
            comments,
            entityScore,
            name,
            email,
            identity,
        ],
    );

    const save = async () => {
        if (!token) return;
        setSaving(true);
        try {
            await axiosInstant.put(publicOrderFeedbackForm(token), buildPayload());
            toast.success('Saved. You can finish this later using the same link.');
        } catch {
            toast.error('We could not save your answers. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const submit = async () => {
        if (!token) return;
        if (!stepDone[0]) {
            toast.error('Please answer every question about your experience.');
            setStep(0);
            return;
        }
        setSaving(true);
        try {
            const { data } = await axiosInstant.post(
                publicSubmitOrderFeedback(token),
                buildPayload(),
            );
            const wasSubmitted = Boolean(form?.submitted);
            setForm(data);
            toast.success(
                wasSubmitted
                    ? 'Thank you, your feedback has been updated.'
                    : 'Thank you, your feedback has been submitted.',
            );
        } catch {
            toast.error('We could not submit your feedback. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return {
        form,
        loading,
        saving,
        invalid,
        step,
        setStep,
        steps,
        stepDone,
        progress,
        orderScores,
        setOrderScore,
        projectScores,
        setProjectScore,
        orderComment,
        projectComment,
        setComment,
        projectCommentKey,
        entityScore,
        setEntityScore,
        name,
        setName,
        email,
        setEmail,
        save,
        submit,
    };
};
